import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseComponent } from '../../../shared/utils/base.component';
import {
  RADIO_YES_NO_OPTIONS,
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../shared/constants/static-data.constants';
import { NgZorroModule } from '../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { differenceInCalendarDays } from 'date-fns';
import { AssociationDetailsAddressComponent } from '../associations/association-details-address/association-details-address.component';
import { LoggerService } from '../../../shared/services/logger.service';
import { SectionsService } from '../../../services/sections.service';
import { EnrollmentDataService } from '../../../services/enrollment-data.service';
import {
  ProviderEnrollmentDataItem,
  ProviderEnrollmentDataResponse,
} from '../../../shared/interfaces/provider.model';
import { parse } from 'date-fns';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { UserService } from '../../../services/user.service';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { MultiselectComponent } from '../../../shared/components/multiselect/multiselect.component';
import { DropdownSelectComponent } from '../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-add-association-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgZorroModule,
    CommonModule,
    NzDatePickerModule,
    AssociationDetailsAddressComponent,
    NzDividerModule,
    DatePickerComponent,
    MultiselectComponent,
    DropdownSelectComponent,
  ],
  templateUrl: './add-association-form.component.html',
  styleUrl: './add-association-form.component.css',
})
export class AddAssociationFormComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  private logger = inject(LoggerService);
  private sectionsService = inject(SectionsService);
  private enrollmentDataSvc = inject(EnrollmentDataService);

  @Input() providerId: number | null = null;
  @Input({ required: true }) sectionName!: string;
  @Input() override validationErrors: any;
  @Input() readonly = false;
  @Input() flowOrigin: 'create' | 'details' = 'details';
  @Input({required: true}) providerType!: string;
  @Input() partiProviderAssocId!: string;
  @Output() formUpdated = new EventEmitter<FormGroup>();

  providerEnrollmentDetails: WritableSignal<any | null> = signal(null);
  isReadonly = computed(() => {
    return !!this.providerEnrollmentDetails();
  });

  infoForm!: FormGroup;
  formReadonly = false;
  permanentAddressErrors: any = {};
  disabledDate!: (current: Date) => boolean;
  gender: any[] = [];
  radioOptions = RADIO_YES_NO_OPTIONS;
  ssnVisible = false;
  relationshipOptions: any;
  eyeColors: any;
  hairColors: any;
  raceOptions: any;
  statesList: any;
  participantRegisterMode: any[] = [];
  taxExemptOptions: any;
  licences: any[] = [];
  services: any[] = [];
  entry: any;
  steps: any;
  sectionDataPath = '';
  flow!: string;
  program: any;
  yesterday: any;
  nprftDocumentationReceivedOptions: any[] = [{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}];
  constructor(private userService: UserService) {
    super()
  }

  ngOnInit(): void {
    this.entry = this.sectionsService.getFlow();
    this.sectionDataPath =
    STATIC_JSON_DATA_PATH.LEAD_DETAILS_PROVIDERS_SECTION_DATA;
    this.getSectionData();
    this.createForm();
    this.disabledDate = (current: Date): boolean => {
      return differenceInCalendarDays(current, new Date()) >= 0;
    };
    this.gender = this.getLookup('Gender');
    this.relationshipOptions = this.getLookup('Relationship');
    this.raceOptions = this.getLookup('Race');
    this.eyeColors = this.getLookup('Eye Color');
    this.hairColors = this.getLookup('Hair Color');
    this.statesList = this.lookupService.getLookupValue('STATE');
    this.participantRegisterMode = this.getLookup('Participant_Register_Mode');
    this.licences = this.getLookup('License_Type');
    this.services = this.getLookup('Service_Type');
    const today = new Date();
    this.yesterday = new Date(today);
    this.yesterday.setDate(today.getDate() - 1);
  }
  
  ngOnChanges(changes: SimpleChanges) {
    console.log("FORM CHANGES", changes)
    this.program = this.userService.getSelectedProgram();
    if (changes['providerId'] && changes['providerId'].currentValue) {
      this.getProviderEnrollmentDetails();
    }
    if (changes['readonly'] && changes['readonly'].currentValue && this.infoForm) {
      this.infoForm.disable();
    }
  }

  getFormControlValue(formControlName: string) {
    return this.infoForm.get(formControlName)?.value ?? null
  }

  getProviderEnrollmentDetails() {
    if (!this.providerId || !this.program) return;

    const payload = {
      programCode: this.program.program_code,
      providerId: this.providerId,
      providerType: this.providerType,
      partiProviderAssocId: this.partiProviderAssocId
    };
    this.enrollmentDataSvc
      .fetchParticipantAssociations(payload)
      .subscribe((response) => {
        console.log('Edit response', response);
        const providerEnrollmentDetailsRes =
          response as ProviderEnrollmentDataResponse;
          const providerData = providerEnrollmentDetailsRes.responsedata;
          this.providerEnrollmentDetails.set({ ...providerData });
          this.populateFormWithDetailsData(this.providerEnrollmentDetails());
      });

  }

  populateFormWithDetailsData(details: ProviderEnrollmentDataItem) {
    const newFormValue = this.mapDetailsDataToForm(details);
    this.infoForm.patchValue(newFormValue);
  }

  createForm(): void {
    const formElements: any = {
      fname: new FormControl(''),
      lname: new FormControl(''),
      dob: new FormControl(null),
      email: new FormControl(''),
      mobile: new FormControl(''),
      permanentAddress: new FormGroup({
        leadId: new FormControl(),
        street1: new FormControl(''),
        street2: new FormControl(''),
        city: new FormControl(''),
        state: new FormControl(''),
        zipCode: new FormControl(''),
        countyOrRegion: new FormControl(''),
        psd_code: new FormControl(''),
        mailing: new FormControl(false),
      }),
    };

    if (
      this.sectionName === STATIC_DATA.ASSOCIATION_AUTHORIZED_REPRESENTATIVE
    ) {
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.additionalDemographics = new FormControl('');
      formElements.race = new FormControl('');
      formElements.heightFeet = new FormControl('');
      formElements.heightInches = new FormControl('');
      formElements.weight = new FormControl('');
      formElements.eyeColor = new FormControl('');
      formElements.hairColor = new FormControl('');
      formElements.birthPlace = new FormControl('');
      formElements.birthCity = new FormControl('');
      formElements.birthState = new FormControl('');
      formElements.birthCountry = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
      formElements.consent = new FormControl(false, Validators.requiredTrue);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_PROVIDER) {
      formElements.mname = new FormControl('');
      formElements.maiden_name = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.NPI = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
      formElements.yearsInState = new FormControl('');
      formElements.emergencyContactFirstName = new FormControl('');
      formElements.emergencyContactLastName = new FormControl('');
      formElements.emergencyContactPhone = new FormControl('');
      formElements.profLicences = new FormControl([]);
      formElements.servicesRendered = new FormControl([]);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_EMPLOYER) {
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_CONTRACTOR) {
      formElements.mname = new FormControl();
      formElements.profLicences = new FormControl([]);
      formElements.servicesRendered = new FormControl([]);
      formElements.taxExempt = new FormControl();
      formElements.nprftDocumentationReceived = new FormControl();
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_VENDOR) {
      formElements.businessName = new FormControl();
      formElements.taxExempt = new FormControl();
      formElements.nprftDocumentationReceived = new FormControl();
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.mname = new FormControl();
      formElements.altPhone = new FormControl('');
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_CONTRACTOR) {
      formElements.mname = new FormControl('');
      formElements.SSN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.taxExempt = new FormControl('');
      formElements.nprftDocumentationReceived = new FormControl('');
      formElements.profLicences = new FormControl([]);
      formElements.servicesRendered = new FormControl([]);
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_LEGAL_GUARDIAN) {
      formElements.mname = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_POWER_OF_ATTORNEY) {
      formElements.mname = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }

    this.infoForm = new FormGroup(formElements);
    this.infoForm.valueChanges.subscribe({
      next: (formGroupValue: FormGroup['value']) => {
        this.formUpdated.emit(this.infoForm);
      },
    });
  }

  async getSectionData() {
    try {
      if (this.entry && this.sectionName) {
        // if (this.flowOrigin === 'create') {
        //   this.sectionDataPath = STATIC_JSON_DATA_PATH.CREATE_CONSUMER_ASSOCIATION;
        //   this.steps = await this.sectionsService.getJsonConfigurationData(
        //     this.sectionDataPath,
        //     this.sectionName
        //   );
        // } else {
      
        // }

        this.steps = await this.sectionsService.getJsonConfigurationData(
          this.sectionDataPath,
          this.entry,
          this.sectionName
        );

  
        this.secData = this.steps;
        /* if (this.sectionName === STATIC_DATA.NEW_EMPLOYER_INFO) {
          this.secData = this.steps[STATIC_DATA.NEW_EMPLOYER_INFO];
        } else if (this.sectionName === STATIC_DATA.AUTHORIZED_REPRESENTATIVE) {
          this.secData = this.steps[STATIC_DATA.AUTHORIZED_REPRESENTATIVE];
        } else if (this.sectionName === STATIC_DATA.INDEPENDENT_PROVIDER) {
          this.secData = this.steps[STATIC_DATA.INDEPENDENT_PROVIDER];
        } else if (this.sectionName === STATIC_DATA.NEW_VENDOR_DETAILS) {
          this.secData = this.steps[STATIC_DATA.NEW_VENDOR_DETAILS];
        } else if (
          this.sectionName === STATIC_DATA.INDEPENDENT_CONTRACTOR_DETAILS
        ) {
          this.secData = this.steps[STATIC_DATA.INDEPENDENT_CONTRACTOR_DETAILS];
        } else if (this.sectionName === STATIC_DATA.LEGAL_GUARDIAN) {
          this.secData = this.steps[STATIC_DATA.LEGAL_GUARDIAN];
        } else if (this.sectionName === STATIC_DATA.POWER_OF_ATTORNEY) {
          this.secData = this.steps[STATIC_DATA.POWER_OF_ATTORNEY];
        } */
      }
    } catch (e) {
      this.logger.debug('Error fetching section details data', e);
    }
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if(value.length > 9) {
      value = value.slice(0,9);
    }

    event.target.value = value;
    this.infoForm.get('SSN')?.setValue(value);
  }
  
  limitMobileDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if(value.length > 10) {
      value = value.slice(0,10);

      event.target.value = value;
      this.infoForm.get('mobile')?.setValue(value);
    }

  }

  limitAltPhoneDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if(value.length > 10) {
      value = value.slice(0,10);

      event.target.value = value;
      this.infoForm.get('altPhone')?.setValue(value);
    }

  }
  
  cancelEdit() {
    this.infoForm.reset();
    this.validationErrors = {};
    this.permanentAddressErrors = {};
    this.formReadonly = false;
    this.infoForm.enable();
    this.readonly = false;
  }

  getFormGroup(controlName: string): FormGroup {
    return this.infoForm.get(controlName) as FormGroup;
  }

  private mapDetailsDataToForm(details: ProviderEnrollmentDataItem): any {
    const basicInfo = details.basicInfo;
    const identifiers = details.basicInfo.identifiers;
    const contactDetails = details.contactDetails;
    const dob = basicInfo.dob;
    return {
      ...basicInfo,
      ...details.communicationPreferences,
      dob: dob 
      ? parse(dob, 'MM/dd/yyyy', new Date())
      : '',
      SSN: this.parseIdentifierValueByType(identifiers, 'SSN'),
      NPI: this.parseIdentifierValueByType(identifiers, 'NPI'),
      email: this.parseContactValueByType(contactDetails, 'email'),
      altPhone: this.parseContactValueByType(contactDetails, 'altPhone'),
      mobile: this.parseContactValueByType(contactDetails, 'mobile'),
      permanentAddress: details.contactDetails?.permanentAddress
    };
  }

  private parseIdentifierValueByType(
    identifiers: ProviderEnrollmentDataItem['basicInfo']['identifiers'] | null,
    type: 'SSN' | 'NPI'
  ): string {
    return identifiers 
    ? identifiers.find((idf) => idf.type === type)?.value ?? ''
    : '';
  }

  private parseContactValueByType(
    contactDetails: ProviderEnrollmentDataItem['contactDetails'] | null,
    type: 'email' | 'altPhone' | 'mobile'
  ): string {
    return contactDetails 
    ? contactDetails.contactMethods.find((cm) => cm.contactMethod === type)?.value ?? ''
    : '';
  }

  getAccessibleInputId(field: string) {
    return `${field} for ${this.sectionName}`;
  }

}
